import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FormattedList, FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import messages from './messages';
import { PrintSettingRow } from './print-settings-row';
import { PrintWarningsSettings } from './print-warnings-settings';
import styles from './styles.module.less';
import { DocumentType } from '../../../enums/document-type';
import { useShippingSuppliesLink, PRINTING } from '../../../feature-flags/hooks/use-enable-shipping-supplies';
import { useEnableSupplyPage } from '../../../feature-flags/hooks/use-enable-supply-page';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';
import { useCheckPrinterStatuses } from '../connect/hooks/use-check-printer-statuses';
import { SuppliesCardContainer } from '../../home-page/version2/supplies-cards/supplies-card-container';
import { SUPPLIES_DISPLAY_LOCATION } from '../../home-page/version2/supplies-cards/supplies-page-constants';

const PrintSettingsDisplayList = [
  DocumentType.Label,
  DocumentType.ReturnLabel,
  DocumentType.PackingSlip,
  DocumentType.EndOfDayForm,
  DocumentType.OtherForms,
  DocumentType.OrderSummary,
  DocumentType.PickList,
  DocumentType.ShipmentManifest,
  DocumentType.CustomsForm,
];

export const PrintSettingsPage = () => {
  const { isShippingSuppliesLinkEnabled, suppliesStoreLink, onShippingSuppliesLinkClicked } = useShippingSuppliesLink(PRINTING);
  const { hasOfflinePrinter, offlinePrinterNames } = useCheckPrinterStatuses();
  const enableSupplyPage = useEnableSupplyPage();

  const bannerMessageValues = useMemo(
    () => ({
      a: (chunks) => <Link to='/settings/connect'>{chunks}</Link>,
      offlinePrinterCount: offlinePrinterNames?.length,
      offlinePrinterNames: <FormattedList type='conjunction' value={offlinePrinterNames} />,
    }),
    [offlinePrinterNames],
  );

  return (
    <div className={styles.content}>
      <SettingsPageTitle value={messages.header} />
      <div className={styles.description}>
        <FormattedMessage {...messages.description} />
        <Tooltip>
          <TooltipContent>
            <Text textType={TextType.Paragraph} value={messages.apryseTooltipText} />
          </TooltipContent>
          <div className={styles['icon-container']}>
            <FontAwesomeIcon className={styles['info-icon']} icon={faInfoCircle} />
          </div>
        </Tooltip>
      </div>
      {hasOfflinePrinter ? (
        <BannerMessageV2
          className={styles['connect-offline-banner']}
          content={<Text formattedMessageValues={bannerMessageValues} value={messages.offlinePrinterBannerText} />}
          status={BannerStatus.Warning}
        />
      ) : null}
      <div className={styles.grid}>
        <div className={styles['header-cell']}>
          <FormattedMessage {...messages.documentTypeHeader} />
        </div>
        <div className={styles['header-cell']}>
          <FormattedMessage {...messages.printToHeader} />
        </div>
        <div className={styles['header-cell']}>
          <FormattedMessage {...messages.documentFormatHeader} />
        </div>
        <div className={styles['header-cell']}>
          <FormattedMessage {...messages.optionsHeader} />
        </div>
        {PrintSettingsDisplayList.map((docType) => (
          <PrintSettingRow key={docType.identifier} docType={docType} />
        ))}
      </div>
      <PrintWarningsSettings />
      {enableSupplyPage && <SuppliesCardContainer page={SUPPLIES_DISPLAY_LOCATION.SETTINGS_PRINTING_SETUP} />}
      {isShippingSuppliesLinkEnabled ? (
        <div className={styles['supplies-store']}>
          <Button href={suppliesStoreLink} onClick={onShippingSuppliesLinkClicked} theme='link'>
            <FormattedMessage {...messages.suppliesStoreLink} />
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </Button>
          <Text textType={TextType.Paragraph}>
            <FormattedMessage {...messages.suppliesStoreBlurb} />
          </Text>
        </div>
      ) : null}
    </div>
  );
};
